'use strict'

import axios from 'axios'
import { observable, action, toJS } from 'mobx'
import { toKSEK, secsToHHMM } from '../../../helpers'

class DashStore {
    @observable reservationChart = {
        data: {
            labels: [],
            datasets: [
                {
                    label: 'Försenat',
                    date: [],
                    data: [],
                    tooltipData: [],
                    orders: [],
                    orderValue: [],
                    backgroundColor: []
                },
                {
                    label: 'Mån',
                    date: [],
                    data: [],
                    tooltipData: [],
                    orders: [],
                    orderValue: [],
                    backgroundColor: []
                },
                {
                    label: 'Tis',
                    date: [],
                    data: [],
                    tooltipData: [],
                    orders: [],
                    orderValue: [],
                    backgroundColor: []
                },
                {
                    label: 'Ons',
                    date: [],
                    data: [],
                    tooltipData: [],
                    orders: [],
                    orderValue: [],
                    backgroundColor: []
                },
                {
                    label: 'Tor',
                    date: [],
                    data: [],
                    tooltipData: [],
                    orders: [],
                    orderValue: [],
                    backgroundColor: []
                },
                {
                    label: 'Fre',
                    date: [],
                    data: [],
                    tooltipData: [],
                    orders: [],
                    orderValue: [],
                    backgroundColor: []
                }
            ]
        },
        options: {
            legend: {
                display: false
            },
            tooltips: {
                titleSpacing: 10,
                xPadding: 15,
                yPadding: 15,
                displayColors: false
            },
            scales: {
                yAxes: [
                    {
                        ticks: {
                            fontColor: 'white',
                            beginAtZero: true,
                            // max: // Dynamically set below
                        },
                    },
                ],
                xAxes: [
                    {
                        ticks: {
                            fontColor: 'white'
                        }
                    }
                ]
            },
        }
    }
    @observable deliveredChart = {
        data: {
            labels: [],
            datasets: [
                {
                    label: 'Summering',
                    data: [],
                    orders: [],
                    productionTimeSeconds: [],
                    backgroundColor: []
                },
            ],
        },
        options: {
            tooltips: {
                titleSpacing: 10,
                xPadding: 15,
                yPadding: 15,
                displayColors: false
            },            
            legend: {
                display: false
            },
            scales: {
                yAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                            fontColor: 'white'
                        },
                    },
                ],
                xAxes: [
                    {
                        ticks: {
                            fontColor: 'white'
                        }
                    }
                ]
            },            
        }    
    }
    @observable overview = {
        order: {
            week: {
                quantity: 0,
                amount: 0,
                time: 0
            },
            month: {
                quantity: 0,
                amount: 0,
                time: 0
            },
            year: {
                quantity: 0,
                amount: 0,
                time: 0
            }         
        },
        delivery: {
            week: {
                quantity: 0,
                amount: 0,
                time: 0
            },
            month: {
                quantity: 0,
                amount: 0,
                time: 0
            },
            year: {
                quantity: 0,
                amount: 0,
                time: 0
            }
        },
        unconfirmed: 0
    }

    months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'Maj',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Okt',
        'Nov',
        'Dec',
    ]

    constructor() {
        if (window.location.pathname === '/home') {
            this.init()
        }
    }

    @action loadDash = async (userId) => {
            this.FETCH_DASHBOARD(userId)
    }

    @action loadReservation = async () => {
        this.FETCH_RESERVATION()
    }

    @action FETCH_DASHBOARD = async (userId) => {
        await axios.get('/dashboard/initDashboard', { params: {
            user_id: userId    
        }}).then((response) => {
            if (response.status === 200 && response.data) {
                if (response.data.reservationChart) {
                    this._setReservation(response.data.reservationChart, response.data.dailyProductionCapacity)
                }

                if (response.data.monthBasedDeliveries) {
                    this._deliveredChart(response.data.monthBasedDeliveries)
                }

                if (response.data.overviews) {
                    this._setOverview(response.data.overviews)
                }
            }
        })
    }

    @action FETCH_RESERVATION = async () => {
        await axios.get('/dashboard/getReservationData', { params: {
            user_id: 0    
        }}).then((response) => {
            if (response.status === 200 && response.data) {
                if (response.data.reservationChart) {
                    this._setReservation(response.data.reservationChart, response.data.dailyProductionCapacity)
                }
            }
        })
    }

    _setReservation = (weeks, dailyProductionCapacity) => {        
        const weeksKeys = Object.keys(weeks).sort()
        const limitInHours = dailyProductionCapacity
        const yellowLimit = limitInHours * 0.75;
        const redLimit = limitInHours * 1;

        const dateFormatOptions = { weekday: 'short', month: 'long', day: 'numeric' };
        const today = new Date().setHours(0,0,0,0);

        this.reservationChart.options.scales.yAxes[0].ticks.max = dailyProductionCapacity;
        if (weeksKeys.length > 0) {
            const firstWeek = weeksKeys[0];

            for (const weekNumber of weeksKeys) {
                
                const weekLabel = `V.${ weekNumber }`

                if (!this.reservationChart.data.labels.find(l => l === weekLabel)) {
                    this.reservationChart.data.labels.push(weekLabel)
                }

                const daysKeys = Object.keys(weeks[weekNumber])

                for (let index = 0; index < daysKeys.length; index++) {

                    const dayKey = daysKeys[index];
                    const day = weeks[weekNumber][dayKey];

                    if (weekNumber === firstWeek) {
                        this.reservationChart.data.datasets[dayKey].date            = []
                        this.reservationChart.data.datasets[dayKey].data            = []
                        this.reservationChart.data.datasets[dayKey].tooltipData     = []
                        this.reservationChart.data.datasets[dayKey].orders          = []   
                        this.reservationChart.data.datasets[dayKey].orderValue      = []   
                        this.reservationChart.data.datasets[dayKey].backgroundColor = []
                    }

                    const dayHours = parseFloat(day['productionTimeSeconds'] / 3600).toFixed(2)
                    const daySeconds = parseFloat(day['productionTimeSeconds']);
                    const orders     = parseFloat(day['numberOfOrders'])
                    const orderValue = parseFloat(day['orderValue'])
                    const date = day['date'] ;
                    let dateAlreadyPassed = false;

                    if (Date.parse(date)) {
                        const thisDate = new Date(date).setHours(0,0,0,0);
                        if (thisDate < today) {
                            dateAlreadyPassed = true;
                        }

                        this.reservationChart.data.datasets[dayKey].date.push(new Date(date).toLocaleDateString('sv-SE',  dateFormatOptions));
                    } else {
                        this.reservationChart.data.datasets[dayKey].date.push('Försenat');
                    }

                    if (dayHours > dailyProductionCapacity) {
                        // TODO: This is hacky so find a better way to make tooltip visible within graph area if daily production capacity is exceeded
                        this.reservationChart.data.datasets[dayKey].data.push(dateAlreadyPassed ? 0 : dailyProductionCapacity)
                        this.reservationChart.data.datasets[dayKey].tooltipData.push(dateAlreadyPassed ? 0 : secsToHHMM(daySeconds))
                    } else {
                        this.reservationChart.data.datasets[dayKey].data.push(dateAlreadyPassed ? 0 : dayHours)
                        this.reservationChart.data.datasets[dayKey].tooltipData.push(dateAlreadyPassed ? 0 : secsToHHMM(daySeconds))
                    }
                    
                    this.reservationChart.data.datasets[dayKey].orders.push(dateAlreadyPassed ? 0 : orders)
                    this.reservationChart.data.datasets[dayKey].orderValue.push(dateAlreadyPassed ? 0 : orderValue)
                    
                    if (dayHours >= redLimit) {
                        this.reservationChart.data.datasets[dayKey].backgroundColor.push('rgb(255, 202, 207)')
                    } else if (dayHours >= yellowLimit) {
                        this.reservationChart.data.datasets[dayKey].backgroundColor.push('rgb(249, 231, 178)')
                    } else {
                        this.reservationChart.data.datasets[dayKey].backgroundColor.push('rgb(180, 218, 189)')
                    }
                }
            }
        }
    }

    _deliveredChart = (months) => {

        const monthKeys = Object.keys(months)

        // Empty
        this.deliveredChart.data.labels = []
        this.deliveredChart.data.datasets[0].data = []
        this.deliveredChart.data.datasets[0].backgroundColor = []
        let firstMonth = true;

        if (monthKeys.length > 0) {
            monthKeys.forEach(monthKey => {
                const month = months[monthKey];
                    // Labels
                    this.deliveredChart.data.labels.push(month.name)
                    // Data
                    this.deliveredChart.data.datasets[0].data.push(month.orderValue)
                    this.deliveredChart.data.datasets[0].orders.push(month.numberOfOrders)
                    this.deliveredChart.data.datasets[0].productionTimeSeconds.push(month.productionTimeSeconds)
                
                if (firstMonth) {
                    this.deliveredChart.data.datasets[0].backgroundColor.push('rgb(41, 167, 69)')
                    firstMonth = false;
                } else {
                    this.deliveredChart.data.datasets[0].backgroundColor.push('rgb(180, 218, 189)')
                }
            });
        }
    }

    _setOverview = (overview) => {
        this.overview.order = overview.order
        this.overview.delivery = overview.delivery
        this.overview.unconfirmed = overview.unconfirmed;

        // Orders - Week
        this.overview.order.week.time = secToHH(this.overview.order.week.time)
        this.overview.order.week.amount = toKSEK(this.overview.order.week.amount)

        // Orders - Month
        this.overview.order.month.time = secToHH(this.overview.order.month.time)
        this.overview.order.month.amount = toKSEK(this.overview.order.month.amount)

        // Orders - Year
        this.overview.order.year.time = secToHH(this.overview.order.year.time)
        this.overview.order.year.amount = toKSEK(this.overview.order.year.amount)

        // Delivery - Week
        this.overview.delivery.week.time = secToHH(this.overview.delivery.week.time)
        this.overview.delivery.week.amount = toKSEK(this.overview.delivery.week.amount)

        // Delivery - Month
        this.overview.delivery.month.time = secToHH(this.overview.delivery.month.time)
        this.overview.delivery.month.amount = toKSEK(this.overview.delivery.month.amount)

        // Delivery - Year
        this.overview.delivery.year.time = secToHH(this.overview.delivery.year.time)
        this.overview.delivery.year.amount = toKSEK(this.overview.delivery.year.amount)     
    }

    init = () => {
        this.FETCH_DASHBOARD()
    }
}

function secToHH(sec) {
    var sec_num = parseInt(sec, 10); // don't forget the second param
    var hours   = Math.floor(sec_num / 3600);
    // var minutes = Math.floor((sec_num - (hours * 3600)) / 60);
    // var seconds = sec_num - (hours * 3600) - (minutes * 60);

    // if (hours   < 10) {hours   = "0"+hours;}
    // if (minutes < 10) {minutes = "0"+minutes;}
    // if (seconds < 10) {seconds = "0"+seconds;}
    return hours;//+':'+minutes+':'+seconds;
}

const DashboardStore = new DashStore()
export default DashboardStore 